import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Spinner, Card, Badge, Accordion } from "react-bootstrap";
import axios from "axios";
import AxisAvatar from "../avatars-256X256/Axis.png";
import AxioAvatar from "../avatars-256X256/Axio.png";
import EchoAvatar from "../avatars-256X256/Echo.png";
import Swal from "sweetalert2";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FUNCTION_URLS } from "../../../config/functionUrls";

// Sub-componente para la tarjeta de Echo en el Orquestador
const EchoSocialCard = ({ data, distributed, onDistribute, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textLimit = 150;
  const isLongText = data.text.length > textLimit;

  return (
    <div className="echo-social-card" style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid rgba(255,255,255,0.1)',
      marginTop: '10px'
    }}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="text-white fw-bold m-0" style={{fontSize: '16px'}}>{data.title}</h5>
            <Badge bg={data.priority === 'high' ? 'danger' : 'info'} size="sm" className="text-uppercase" style={{fontSize: '8px'}}>
               {data.type}
            </Badge>
        </div>
        <div className="text-white-50 small mb-2" style={{lineHeight: '1.5', fontSize: '12.5px'}}>
          {isExpanded || !isLongText ? (
            <p className="m-0" style={{ whiteSpace: 'pre-line' }}>{data.text}</p>
          ) : (
            <p className="m-0">
              {data.text.substring(0, textLimit)}...
              <span 
                onClick={() => setIsExpanded(true)} 
                style={{ color: '#00c6ff', cursor: 'pointer', fontWeight: 'bold', marginLeft: '5px' }}
              >
                Ver más
              </span>
            </p>
          )}
          {isExpanded && isLongText && (
            <span 
              onClick={() => setIsExpanded(false)} 
              style={{ color: '#00c6ff', cursor: 'pointer', fontWeight: 'bold', display: 'block', marginTop: '5px' }}
            >
              Ver menos
            </span>
          )}
        </div>
      </div>
      
      <div style={{ 
          position: 'relative', 
          width: '350px', 
          margin: '0 auto',
          minHeight: '350px', 
          maxHeight: '620px', 
          background: '#000', 
          overflowY: 'auto',
          borderRadius: '12px',
          border: '1px solid #333'
      }}>
          {/* Centered vertical image with proportional scaling */}
          <img
            src={data.imageUrl}
            style={{ width: '100%', height: 'auto', display: 'block' }}
            alt="Visual"
            onError={(e) => {
              if (data.imageUrlFallback && e.currentTarget.src !== data.imageUrlFallback) {
                e.currentTarget.src = data.imageUrlFallback;
              }
            }}
          />
      </div>

      <div className="p-2 px-3 bg-dark d-flex justify-content-between align-items-center border-top border-secondary">
          <small className="text-muted" style={{fontSize: '10px'}}>
             {distributed ? '✅ Distribuido' : 'Pendiente de aprobación'}
          </small>
          <Button 
             variant={distributed ? "success" : "primary"} 
             size="sm" 
             pill 
             className="rounded-pill px-3" 
             style={{fontSize: '11px', fontWeight: 'bold'}}
             onClick={() => !distributed && onDistribute()}
             disabled={loading || distributed}
          >
            {distributed ? '✅ Enviado' : 'Publicar Ahora'}
          </Button>
      </div>
    </div>
  );
};

const AxisChat = ({ usuario }) => {
  const [messages, setMessages] = useState([
    { 
      sender: 'axis', 
      text: 'Soy el orquestador de la red de agentes de Neurum OS. ¿En qué puedo coordinar para ti hoy?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendQuestion = async (text) => {
    const msg = text || question;
    if (!msg.trim()) return;

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMessage = { sender: 'user', text: msg, timestamp };
    
    setMessages(prev => [...prev, userMessage]);
    if (!text) setQuestion('');
    setLoading(true);

    try {
      // Llamar al orquestador Axis
      // Se utiliza el DNI y RUC del usuario para contexto
      const response = await axios.post(FUNCTION_URLS.axisOrquestadorLegacy, {
        From: usuario?.dni || "WEB_USER",
        Body: userMessage.text,
        RUC: usuario?.ruc || usuario?.rucEmpresa
      });

      // Axis responde con TwiML en producción, pero aquí asumimos que manejamos la respuesta de texto
      // Si la API devuelve un string (Twilio XML), extraemos el mensaje.
      // Pero como estamos en el frontend, probablemente queramos una respuesta JSON.
      // Nota: Si axis_Orquestador devuelve XML, necesitaremos parsearlo o actualizar la función para JSON.
      
      let axisText = "No recibí una respuesta clara.";
      let suggestions = [];
      if (typeof response.data === 'string' && response.data.includes('<Message>')) {
          const match = response.data.match(/<Message>(.*?)<\/Message>/s);
          axisText = match ? match[1] : response.data;
      } else {
          axisText = response.data.answer || response.data;
      }

      if (typeof axisText === 'string') {
        suggestions = axisText.split('\n')
          .filter(line => line.trim().startsWith('🔹'))
          .map(line => line.replace(/^🔹\s*/, '').trim());
        if (suggestions.length > 0) {
          axisText = axisText.split('\n').filter(line => !line.trim().startsWith('🔹')).join('\n').trim();
        }
      }

      const axisMessage = { 
        sender: 'axis', 
        text: axisText,
        suggestions: suggestions,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, axisMessage]);
    } catch (error) {
      console.error("Error en Axis Chat:", error);
      const errorMessage = { 
        sender: 'axis', 
        text: `Error de conexión: ${error.message}. Por favor revisa la configuración del orquestador.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleDistribute = async (data, msgIdx) => {
      const result = await Swal.fire({
          title: '¿Distribuir Comunicado?',
          text: `Se enviará a todos los colaboradores del RUC: ${usuario.ruc}`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, enviar a todos',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#2563eb'
      });

      if (result.isConfirmed) {
          setLoading(true);
          try {
              await axios.post(FUNCTION_URLS.echoDistributeAnnouncement, {
                  announcement: data,
                  ruc: usuario?.ruc || usuario?.rucEmpresa,
                  senderDni: usuario?.dni
              });

              Swal.fire('¡Éxito!', 'Comunicado distribuido correctamente.', 'success');
              
              setMessages(prev => {
                  const newMsgs = [...prev];
                  newMsgs[msgIdx] = { ...newMsgs[msgIdx], distributed: true };
                  return newMsgs;
              });
          } catch (error) {
              console.error("Error distribuyendo:", error);
              Swal.fire('Error', 'No se pudo completar la distribución masiva.', 'error');
          } finally {
              setLoading(false);
          }
      }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const styles = {
    chatContainer: {
      maxWidth: '900px',
      margin: '20px auto',
      height: '1500px',
      background: '#121212',
      borderRadius: '24px',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
      border: '1px solid #333'
    },
    chatHeader: {
      background: 'linear-gradient(90deg, #1f1c2c 0%, #928dab 100%)',
      color: 'white',
      padding: '20px 25px',
      borderBottom: '1px solid #333'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    avatar: {
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      border: '2px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
      background: '#000'
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '50%'
    },
    chatTitle: {
      margin: 0,
      fontSize: '20px',
      fontWeight: '700',
      letterSpacing: '0.5px'
    },
    chatSubtitle: {
      margin: 0,
      fontSize: '13px',
      color: 'rgba(255, 255, 255, 0.7)'
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '25px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      background: '#0f0f0f'
    },
    messageWrapper: {
      display: 'flex',
      width: '100%'
    },
    messageWrapperUser: {
      justifyContent: 'flex-end'
    },
    messageWrapperAxis: {
      justifyContent: 'flex-start',
      gap: '12px'
    },
    messageBubble: {
      maxWidth: '75%',
      padding: '14px 20px',
      borderRadius: '20px',
      fontSize: '14.5px',
      lineHeight: '1.5',
      position: 'relative'
    },
    messageBubbleUser: {
      background: '#007bff',
      color: 'white',
      borderBottomRightRadius: '4px'
    },
    messageBubbleAxis: {
      background: '#242424',
      color: '#e0e0e0',
      borderBottomLeftRadius: '4px',
      border: '1px solid #333'
    },
    messageTime: {
      fontSize: '10px',
      opacity: 0.5,
      marginTop: '5px',
      display: 'block',
      textAlign: 'right'
    },
    inputArea: {
      padding: '20px 25px',
      background: '#1a1a1a',
      borderTop: '1px solid #333'
    },
    inputGroup: {
      display: 'flex',
      gap: '15px',
      alignItems: 'center'
    },
    textarea: {
      flex: 1,
      background: '#2a2a2a',
      border: '1px solid #444',
      borderRadius: '30px',
      padding: '12px 25px',
      color: 'white',
      resize: 'none',
      outline: 'none',
      fontSize: '14px'
    },
    sendButton: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      background: '#007bff',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s',
      cursor: 'pointer'
    },
    axisAvatarSmall: {
       width: '32px',
       height: '32px',
       borderRadius: '50%',
       background: '#000',
       flexShrink: 0
    }
  };

  return (
    <div style={styles.chatContainer}>
      <div style={styles.chatHeader}>
        <div style={styles.headerContent}>
          <div style={styles.avatar}>
            <img src={AxisAvatar} alt="Axis" style={styles.avatarImage} />
          </div>
          <div>
            <h4 style={styles.chatTitle}>Axis Orquestador</h4>
            <p style={styles.chatSubtitle}>Guía Principal de Neurum OS • Sistema Operativo Cognitivo</p>
          </div>
        </div>
      </div>

      <div style={styles.messagesContainer}>
        {messages.map((msg, idx) => {
          // Intentar parsear el texto si es JSON para renderizado especial
          let scrumData = null;
          let echoData = null; // Para comunicaciones
          let isJson = false;
          let agentIcon = AxisAvatar;

          if (msg.sender === 'axis') {
            const jsonMatch = msg.text.match(/\{.*\}/s);
            if (jsonMatch) {
              try {
                const parsed = JSON.parse(jsonMatch[0]);
                if (parsed.epic && parsed.stories) {
                  scrumData = parsed;
                  isJson = true;
                  agentIcon = AxioAvatar;
                } else if (parsed.title && parsed.imageUrl) {
                  echoData = parsed;
                  isJson = true;
                  agentIcon = EchoAvatar;
                }
              } catch (e) {
                // No era JSON válido
              }
            }
          }

          return (
            <div key={idx} style={{
              ...styles.messageWrapper,
              ...(msg.sender === 'user' ? styles.messageWrapperUser : styles.messageWrapperAxis)
            }}>
              {msg.sender === 'axis' && (
                  <div style={styles.axisAvatarSmall}>
                      <img 
                        src={agentIcon} 
                        style={styles.avatarImage} 
                        alt="A" 
                      />
                  </div>
              )}
              
              <div style={{
                ...styles.messageBubble,
                ...(msg.sender === 'user' ? styles.messageBubbleUser : styles.messageBubbleAxis),
                ...(msg.isError ? { background: '#4a1515', borderColor: '#8b0000' } : {}),
                ...(isJson ? { maxWidth: '85%', background: '#1a1a1a', border: '1px solid #444' } : {})
              }}>

                {/* Renderizado Normal o Especial */}
                {scrumData ? (
                  <div className="scrum-render">
                    <div className="d-flex align-items-center mb-3">
                      <Badge bg="primary" className="me-2">ÉPICA</Badge>
                      <h6 className="mb-0 text-white fw-bold">{scrumData.epic.title}</h6>
                    </div>
                    <p className="small text-muted">{scrumData.epic.description}</p>
                    
                    <Accordion defaultActiveKey="0" className="mt-3">
                      {scrumData.stories.map((story, sIdx) => (
                        <Accordion.Item eventKey={sIdx.toString()} key={sIdx} className="bg-dark border-secondary">
                          <Accordion.Header className="bg-dark text-white">
                            <span className="small fw-semibold text-info">{story.title}</span>
                          </Accordion.Header>
                          <Accordion.Body className="bg-dark text-light p-2">
                            <ul className="list-unstyled mb-0">
                                {story.tasks.map((task, tIdx) => (
                                    <li key={tIdx} className="mb-2 d-flex align-items-start small p-2 rounded hover-bg" style={{background: 'rgba(255,255,255,0.05)'}}>
                                        <div className="me-2 mt-1">
                                            <div style={{
                                                width: '16px', 
                                                height: '16px', 
                                                border: '1px solid #00c6ff', 
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}></div>
                                        </div>
                                        <div>
                                            <span className="d-block fw-bold text-white-50">{task.title}</span>
                                            <span style={{fontSize: '11px'}} className="text-muted">{task.description}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>

                    <div className="mt-3 p-2 bg-dark rounded d-flex justify-content-between align-items-center border border-secondary border-opacity-50">
                        <small className="text-muted" style={{fontSize: '11px'}}>✓ Épica y Tareas auto-aprobadas bajo estado BACKLOG.</small>
                        <Button variant="outline-info" size="sm" style={{fontSize: '11px', borderRadius: '20px'}} onClick={() => window.location.href='/planillas' /* o la ruta del tablero Kanban de ellos */}>
                            Revisar en Módulo
                        </Button>
                    </div>
                  </div>
                ) : echoData ? (
                  <EchoSocialCard 
                    data={echoData} 
                    distributed={msg.distributed} 
                    onDistribute={() => handleDistribute(echoData, idx)}
                    loading={loading}
                  />
                ) : (
                  msg.sender === 'axis' ? (
                    <React.Fragment>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({children}) => <span style={{display:'block', marginBottom:'0.5rem', lineHeight:1.5}}>{children}</span>,
                          strong: ({children}) => <strong style={{color:'#fff'}}>{children}</strong>,
                          ul: ({children}) => <ul style={{paddingLeft:'1.2rem', marginBottom:'0.5rem'}}>{children}</ul>,
                          ol: ({children}) => <ol style={{paddingLeft:'1.2rem', marginBottom:'0.5rem'}}>{children}</ol>,
                          li: ({children}) => <li style={{marginBottom:'0.25rem'}}>{children}</li>,
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div style={{marginTop:'0.75rem'}}>
                          {msg.suggestions.map((q, i) => (
                            <button
                              key={i}
                            onClick={() => sendQuestion(q)}
                              style={{
                                display:'block',
                                width:'100%',
                                textAlign:'left',
                                padding:'0.5rem 0.75rem',
                                marginBottom:'0.35rem',
                                border:'1px solid rgba(13,110,253,0.3)',
                                borderRadius:'8px',
                                background:'rgba(13,110,253,0.08)',
                                color:'#8ab4f8',
                                cursor:'pointer',
                                fontSize:'13px',
                                lineHeight:1.4,
                                transition:'background 0.2s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(13,110,253,0.18)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'rgba(13,110,253,0.08)'}
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ) : (
                    <span>{msg.text}</span>
                  )
                )}

                <span style={styles.messageTime}>{msg.timestamp}</span>
              </div>
            </div>
          );
        })}
        {loading && (
          <div style={{...styles.messageWrapper, ...styles.messageWrapperAxis}}>
            <div style={styles.axisAvatarSmall}>
                <img src={AxisAvatar} style={styles.avatarImage} alt="A" />
            </div>
            <div style={{...styles.messageBubble, ...styles.messageBubbleAxis}}>
              <Spinner animation="border" size="sm" variant="info" />
              <span className="ms-2" style={{fontSize: '12px'}}>Coordinando agentes...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={styles.inputArea}>
        <Form onSubmit={(e) => { e.preventDefault(); sendQuestion(); }}>
          <div style={styles.inputGroup}>
            <textarea
              ref={inputRef}
              style={styles.textarea}
              placeholder="Habla con el orquestador..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              rows={1}
            />
            <button 
              type="submit" 
              style={{
                ...styles.sendButton,
                opacity: (loading || !question.trim()) ? 0.6 : 1
              }}
              disabled={loading || !question.trim()}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AxisChat;
